import type { Block, BlockType, Deck, FlowEdge, Reference, Section, SectionPlan } from "@hali-shares/deck-schema";
import { PLACEHOLDER_TITLE, blockText, hostOf, isEmptySection } from "@hali-shares/deck-schema";

export { blockText, hostOf };

export type Chip = "CONCEPT" | "FLOW" | "POLL" | "QUIZ" | "SCREENSHOT" | "VIDEO" | "TIMELINE" | "REFERENCE";

export function chipOf(type: BlockType): Chip {
  return type === "references" ? "REFERENCE" : (type.toUpperCase() as Chip);
}

export function slugify(text: string, max = 40): string {
  const s = text
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, max)
    .replace(/-+$/, "");
  return /^[a-z0-9]/.test(s) ? s : "item";
}

export function uniqueId(base: string, taken: Iterable<string>): string {
  const set = new Set(taken);
  const b = slugify(base) || "item";
  let id = b;
  for (let n = 2; set.has(id); n++) id = `${b}-${n}`;
  return id;
}

export function blockIds(deck: Deck): string[] {
  return deck.sections.flatMap((s) => s.blocks.map((b) => b.id));
}

export function minutesTotal(deck: Deck): number {
  return deck.sections.reduce((n, s) => n + s.minutes, 0);
}

/** "1 flow · 1 poll" for the outline. References count both "Read more" blocks and cited references. */
export function sectionSummary(section: Section): string {
  const counts = new Map<string, number>();
  for (const b of section.blocks) {
    const k = chipOf(b.type).toLowerCase();
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  const cited = citedRefs(section).length;
  if (cited) counts.set("reference", (counts.get("reference") ?? 0) + cited);
  return [...counts].map(([k, n]) => `${n} ${k}${n > 1 ? "s" : ""}`).join(" · ");
}

/** Reference ids cited anywhere in a section, in first-cited order. */
export function citedRefs(section: Section): string[] {
  const out: string[] = [];
  for (const b of section.blocks) {
    const ids = [
      ...("refs" in b ? b.refs : []),
      ...(b.type === "flow" ? b.nodes.flatMap((n) => n.detail?.refs ?? []) : []),
      ...(b.type === "timeline" ? b.items.flatMap((i) => i.refs) : []),
    ];
    for (const id of ids) if (!out.includes(id)) out.push(id);
  }
  return out;
}

export function blockMeta(b: Block, deck: Deck): string {
  switch (b.type) {
    case "concept":
      return b.statement
        ? "One statement"
        : b.bullets.length
          ? `${b.bullets.length} bullets`
          : (b.lead ?? "Title and lead");
    case "flow": {
      const zoom = b.nodes.filter((n) => n.detail).map((n) => n.label);
      return `${b.nodes.length} steps${zoom.length ? ` · zoom into ${zoom.join(", ")}` : ""}`;
    }
    case "poll":
      return b.kind === "scale"
        ? `Scale ${b.scale?.min ?? 1}–${b.scale?.max ?? 5}`
        : b.kind === "open"
          ? "Open text"
          : b.kind === "wordcloud"
            ? "Word cloud"
            : `${b.options.length} options${b.kind === "multi" ? ", pick several" : ""}`;
    case "quiz":
      return `${b.options.length} options, 1 correct`;
    case "screenshot":
      return `${b.filename || "No file named"} · ${b.pins.length} discussion pin${b.pins.length === 1 ? "" : "s"}${b.assetId ? "" : " · no image yet"}`;
    case "video":
      return [
        b.filename || "No file named",
        b.start !== undefined || b.end !== undefined ? `${clipTime(b.start ?? 0)}–${b.end !== undefined ? clipTime(b.end) : "end"}` : null,
        b.loop ? "loops" : "plays once",
        b.assetId ? null : "no clip yet",
      ]
        .filter(Boolean)
        .join(" · ");
    case "timeline": {
      const span = `${b.items[0]?.when ?? ""} to ${b.items[b.items.length - 1]?.when ?? ""}`;
      return `${b.items.length} points · ${span} · ${b.reveal === "steps" ? "one at a time" : "all at once"}`;
    }
    case "references":
      return b.referenceIds
        ? `${b.referenceIds.length} references`
        : `All ${deck.references.length} references, on screen and on phones`;
  }
}

/** Seconds as "1:05", or "0:12.5" when it isn't a whole second. */
export function clipTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds - m * 60;
  return `${m}:${s < 10 ? "0" : ""}${Number.isInteger(s) ? s : s.toFixed(1)}`;
}

export function referenceMeta(r: Reference): string {
  return [r.authors, hostOf(r.url)].filter(Boolean).join(" · ");
}

export const ADDABLE: { type: BlockType | "reference"; label: string }[] = [
  { type: "concept", label: "Concept" },
  { type: "flow", label: "Flow" },
  { type: "poll", label: "Poll" },
  { type: "quiz", label: "Quiz" },
  { type: "screenshot", label: "Screenshot" },
  { type: "video", label: "Video" },
  { type: "timeline", label: "Timeline" },
  { type: "reference", label: "Reference" },
];

/** A valid starting block of each type, with an id unique in the deck. */
export function newBlock(type: BlockType, deck: Deck): Block {
  const id = (base: string) => uniqueId(base, blockIds(deck));
  switch (type) {
    case "concept":
      return { id: id("concept"), type, title: "New concept", bullets: [], refs: [] };
    case "flow":
      return {
        id: id("flow"),
        type,
        title: "New flow",
        nodes: [
          { id: "step-1", label: "Step 1" },
          { id: "step-2", label: "Step 2" },
          { id: "step-3", label: "Step 3" },
        ],
        edges: [
          { from: "step-1", to: "step-2", kind: "forward" },
          { from: "step-2", to: "step-3", kind: "forward" },
        ],
        refs: [],
      };
    case "poll":
      return {
        id: id("poll"),
        type,
        question: "New question?",
        kind: "single",
        options: [
          { id: "a", label: "First option" },
          { id: "b", label: "Second option" },
        ],
      };
    case "quiz":
      return {
        id: id("quiz"),
        type,
        question: "New quiz question?",
        options: [
          { id: "a", label: "Right answer" },
          { id: "b", label: "Tempting wrong answer" },
        ],
        correctOptionId: "a",
      };
    case "screenshot":
      return { id: id("screenshot"), type, title: "Discuss: ", assetId: null, filename: "", pins: [], refs: [] };
    case "video":
      return { id: id("video"), type, title: "Watch: ", assetId: null, filename: "", loop: true, muted: true, refs: [] };
    case "timeline":
      return {
        id: id("timeline"),
        type,
        title: "New timeline",
        items: [
          { id: "point-1", when: "2020", label: "First", refs: [] },
          { id: "point-2", when: "2022", label: "Then", refs: [] },
          { id: "point-3", when: "2024", label: "Now", refs: [] },
        ],
        reveal: "steps",
        refs: [],
      };
    case "references":
      return { id: id("read-more"), type, title: "Read more" };
  }
}

/* ---------- flow connections ---------- */

/**
 * The usual shape of a flow: each step leads to the next, and optionally the last loops back to the first. When a
 * flow has that shape the editor shows two controls instead of a list of arrows. Null when it has any other arrow.
 */
export function inOrderShape(nodeIds: string[], edges: FlowEdge[]): { loop: boolean; loopLabel?: string } | null {
  const chain = chainEdges(nodeIds);
  const forward = edges.filter((e) => e.kind === "forward");
  const loops = edges.filter((e) => e.kind === "loop");
  const isChain =
    forward.length === chain.length &&
    forward.every((e) => !e.label) &&
    chain.every((c) => forward.some((e) => e.from === c.from && e.to === c.to));
  if (!isChain || loops.length > 1) return null;
  const loop = loops[0];
  if (loop && (loop.from !== nodeIds[nodeIds.length - 1] || loop.to !== nodeIds[0])) return null;
  return loop ? { loop: true, ...(loop.label && { loopLabel: loop.label }) } : { loop: false };
}

/** The arrows for the usual shape: steps in order, plus the loop back when asked. */
export function inOrderEdges(nodeIds: string[], loop: boolean, loopLabel?: string): FlowEdge[] {
  const edges = chainEdges(nodeIds);
  if (loop && nodeIds.length >= 2)
    edges.push({
      from: nodeIds[nodeIds.length - 1]!,
      to: nodeIds[0]!,
      kind: "loop",
      ...(loopLabel && { label: loopLabel }),
    });
  return edges;
}

/** Forward edges joining steps in order: the usual case. */
export function chainEdges(nodeIds: string[]): FlowEdge[] {
  return nodeIds.slice(1).map((to, i) => ({ from: nodeIds[i]!, to, kind: "forward" as const }));
}

/** Zod issue paths for one block, made relative ("nodes.0.label"), for the drawer. */
export function issuesFor(issues: { path: string; message: string }[], sectionIndex: number, blockIndex?: number) {
  const prefix =
    blockIndex === undefined ? `sections.${sectionIndex}` : `sections.${sectionIndex}.blocks.${blockIndex}`;
  return issues
    .filter((i) => i.path === prefix || i.path.startsWith(`${prefix}.`))
    .map((i) => ({ path: i.path.slice(prefix.length + 1), message: i.message }));
}

/** After steps are reordered: if the arrows simply joined the old order, join the new one instead. */
export function rewireAfterMove(before: string[], after: string[], edges: FlowEdge[]): FlowEdge[] | null {
  const forward = edges.filter((e) => e.kind === "forward" && !e.label);
  const others = edges.filter((e) => !(e.kind === "forward" && !e.label));
  const chain = chainEdges(before);
  const same =
    forward.length === chain.length && chain.every((c) => forward.some((e) => e.from === c.from && e.to === c.to));
  return same ? [...chainEdges(after), ...others] : null;
}

/* ---------- converting a slide to another type ---------- */

export const CONVERTIBLE: Partial<Record<BlockType, BlockType[]>> = {
  concept: ["flow"],
  flow: ["concept"],
  poll: ["quiz"],
  quiz: ["poll"],
};

/**
 * Carries a block's content into another type by hand (no AI), where nothing is lost: a flow's steps become bullets,
 * a poll becomes a quiz and back. Null otherwise; a list can't become a sequence mechanically (which items lead to
 * which is a judgement), so concept → flow goes to the model.
 */
export function convertBlock(b: Block, to: BlockType): Block | null {
  const notes = b.notes ? { notes: b.notes } : {};
  if (b.type === "flow" && to === "concept") {
    const lines = b.nodes.map((n) => (n.caption ? `${n.label}: ${n.caption}` : n.label).slice(0, 120));
    const extra = lines.slice(4);
    const moreNotes = extra.length ? `Also: ${extra.join("; ")}.` : "";
    const joined = [b.notes, moreNotes].filter(Boolean).join("\n\n").slice(0, 2000);
    return {
      id: b.id,
      type: "concept",
      title: b.title,
      ...(b.lead && { lead: b.lead }),
      bullets: lines.slice(0, 4),
      refs: b.refs,
      ...(joined && { notes: joined }),
    };
  }
  if (b.type === "poll" && to === "quiz") {
    if (b.options.length < 2) return null;
    return {
      id: b.id,
      type: "quiz",
      question: b.question,
      options: b.options,
      correctOptionId: b.options[0]!.id,
      ...notes,
    };
  }
  if (b.type === "quiz" && to === "poll") {
    return { id: b.id, type: "poll", question: b.question, kind: "single", options: b.options, ...notes };
  }
  return null;
}

/* ---------- the guided studio ---------- */

export { isEmptySection };

export type SectionStatus = "empty" | "drafted" | "reviewed";

export function sectionStatus(s: Section, plan: SectionPlan | undefined): SectionStatus {
  if (isEmptySection(s)) return "empty";
  return plan?.reviewed ? "reviewed" : "drafted";
}

/** A blank talk: the one placeholder section a new talk starts with. */
export function isBlankDeck(deck: Deck): boolean {
  return deck.sections.length === 1 && isEmptySection(deck.sections[0]!) && deck.sections[0]!.ahead.length === 0;
}

/** What each slide type is for, shown where the presenter picks one. */
export const SLIDE_KINDS: { type: BlockType | "reference"; label: string; use: string }[] = [
  { type: "concept", label: "Concept", use: "One idea: a few bullets, or one big statement." },
  { type: "flow", label: "Flow", use: "Steps that happen in order: a process, a pipeline, a loop." },
  { type: "poll", label: "Poll", use: "Ask the room about their own work. Phones vote live." },
  { type: "quiz", label: "Quiz", use: "Test one common misconception, then reveal the answer." },
  { type: "screenshot", label: "Screenshot", use: "Discuss an image, with numbered questions pinned on it." },
  { type: "video", label: "Video", use: "A short clip or GIF that shows it moving. Plays on the projector." },
  { type: "timeline", label: "Timeline", use: "How something changed over time: dated points, revealed one by one." },
  { type: "references", label: "Read more", use: "The links people should keep, on one slide." },
  { type: "reference", label: "Reference", use: "A paper or page to cite as a footnote." },
];

/** One row of the outline editor: a section's title, minutes and points, and its intent from the plan. */
export type OutlineRow = { id: string; title: string; minutes: number; points: string[]; intent: string };

export function outlineRows(deck: Deck, plan: Record<string, SectionPlan> = {}): OutlineRow[] {
  if (isBlankDeck(deck)) return [];
  return deck.sections.map((s) => ({
    id: s.id,
    title: s.title,
    minutes: s.minutes,
    points: s.ahead,
    intent: plan[s.id]?.intent ?? "",
  }));
}

/** An id for a new outline row, from its title, unique among the rows and the deck's sections. */
export function newSectionId(title: string, rows: OutlineRow[], deck: Deck): string {
  return uniqueId(slugify(title || "section", 40), [...rows.map((r) => r.id), ...deck.sections.map((s) => s.id)]);
}

/**
 * The deck with the outline applied: sections in the rows' order, kept sections keep their slides, new ones get a
 * "[To write]" placeholder. `dropped` are sections left out that had slides (worth a confirmation).
 */
export function applyOutline(deck: Deck, rows: OutlineRow[]): { deck: Deck; dropped: Section[] } {
  const byId = new Map(deck.sections.map((s) => [s.id, s]));
  const kept = new Set(rows.map((r) => r.id));
  const taken = new Set(rows.flatMap((r) => byId.get(r.id)?.blocks.map((b) => b.id) ?? []));
  const sections: Section[] = rows.map((r) => {
    const ahead = r.points.map((p) => p.trim()).filter(Boolean);
    const s = byId.get(r.id);
    if (s) return { ...s, title: r.title.trim(), minutes: r.minutes, ahead };
    const id = uniqueId(`${r.id}-draft`, taken);
    taken.add(id);
    return {
      id: r.id,
      title: r.title.trim(),
      minutes: r.minutes,
      ahead,
      blocks: [{ id, type: "concept", title: PLACEHOLDER_TITLE, bullets: [], refs: [] }],
    };
  });
  return { deck: { ...deck, sections }, dropped: deck.sections.filter((s) => !kept.has(s.id) && !isEmptySection(s)) };
}

/** The plan after an outline save: intents from the rows, review marks kept, removed sections forgotten. */
export function planFromRows(rows: OutlineRow[], plan: Record<string, SectionPlan> = {}): Record<string, SectionPlan> {
  return Object.fromEntries(
    rows.map((r) => {
      const intent = r.intent.trim();
      const reviewed = plan[r.id]?.reviewed;
      return [r.id, { ...(intent && { intent }), ...(reviewed && { reviewed }) }];
    }),
  );
}
